import WebViewer from "@pdftron/webviewer";
import { useQuery } from "@tanstack/react-query";
import { FileText } from "lucide-react";
import {
  type ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";

import { useTheme } from "@/components/ThemeProvider";
import { isDarkTheme } from "@/lib/themes";
import { Button, LoadingSpinner, Typography } from "@/components/ui";
import { generateS3ItemPreSignedUrlV1 } from "@/lib/data/requests/s3";
import { queryKeys } from "@/lib/data/static";
import { cn } from "@/lib/utils/cn";
import { getFileNameParts } from "@/lib/utils/matterFile";

export const PDF_MIME_TYPE = "application/pdf";
const PDF_EDITOR_DEBUG_PREFIX = "[PdfEditor]";
const DEFAULT_STICKY_NOTE_COLOR = "#9CA3AF";
const DEFAULT_PDF_EDITOR_ZOOM = 0.85;
const REDACTION_SEARCH_PATTERNS = [
  {
    label: "Credit cards",
    type: "creditCards",
    regex: /\b(?:\d[ -]*?){13,16}\b/g,
  },
  {
    label: "DOB",
    type: "dateOfBirth",
    regex:
      /\b(?:0?[1-9]|1[0-2])[/-](?:0?[1-9]|[12]\d|3[01])[/-](?:\d{2}|\d{4})\b/g,
  },
  {
    label: "Dollar amounts",
    type: "dollarAmounts",
    regex: /\$\s?(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d{2})?\b/g,
  },
  {
    label: "Emails",
    type: "emails",
    regex: /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi,
  },
  {
    label: "Phone numbers",
    type: "phoneNumbers",
    regex: /\b(?:\+?1[-.\s]?)?(?:\(?\d{3}\)?[-.\s]?)\d{3}[-.\s]?\d{4}\b/g,
  },
  {
    label: "SSN",
    type: "socialSecurityNumbers",
    regex: /\b\d{3}-?\d{2}-?\d{4}\b/g,
  },
] as const;

const logPdfEditorDebug = (
  message: string,
  payload?: Record<string, unknown>,
) => {
  if (payload) {
    console.debug(PDF_EDITOR_DEBUG_PREFIX, message, payload);
    return;
  }
  console.debug(PDF_EDITOR_DEBUG_PREFIX, message);
};

const getBlobDebugSummary = async (blob: Blob) => {
  const summary: Record<string, unknown> = {
    size: blob.size,
    type: blob.type,
  };

  if (typeof window === "undefined" || typeof TextEncoder === "undefined") {
    return summary;
  }

  try {
    const arrayBuffer = await blob.arrayBuffer();
    const digest = await window.crypto.subtle.digest("SHA-256", arrayBuffer);
    const hash = Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
    summary.sha256 = hash;
  } catch (error) {
    summary.hashError =
      error instanceof Error ? error.message : "Unknown hash error";
  }

  return summary;
};

export type PdfEditorHandle = {
  exportDocument: (options?: PdfEditorExportOptions) => Promise<Blob | null>;
  hasPendingRedactions: () => boolean;
  hasAppliedRedactions: () => boolean;
  isReady: boolean;
  isLoading: boolean;
};

export type PdfEditorExportOptions = {
  applyRedactions?: boolean;
};

export type PdfEditorFileType =
  | "matter-file"
  | "firm-file"
  | "matter-template"
  | "matter-signature-request-file"
  | "firm-signature-request-file"
  | "matter-share-folder-file";

const findElementInOpenShadowRoots = (
  root: ParentNode,
  selector: string,
): HTMLElement | null => {
  const directMatch =
    "querySelector" in root ? root.querySelector(selector) : null;
  if (directMatch instanceof HTMLElement) {
    return directMatch;
  }

  if (!("querySelectorAll" in root)) {
    return null;
  }

  const elements = Array.from(root.querySelectorAll("*"));
  for (const element of elements) {
    if (element instanceof Element && element.shadowRoot) {
      const nestedMatch = findElementInOpenShadowRoots(
        element.shadowRoot,
        selector,
      );
      if (nestedMatch) {
        return nestedMatch;
      }
    }
  }

  return null;
};

type PdfEditorProps = {
  fileName: string;
  presignedUrlKey?: string;
  fileType: PdfEditorFileType;
  annotationUserName?: string;
  initialDocUrl?: string;
  licenseKey?: string;
  canSave?: boolean;
  onSave?: (payload: { blob: Blob; fileName: string }) => Promise<void> | void;
  showSaveButton?: boolean;
  showHeader?: boolean;
  headerActions?: React.ReactNode;
  onReadyChange?: (ready: boolean) => void;
  onAppliedRedactionsChange?: (applied: boolean) => void;
  onScrollContainerReady?: (container: HTMLDivElement | null) => void;
  isActive?: boolean;
  className?: string;
};

export const PdfEditor = forwardRef(function PdfEditor(
  {
    fileName,
    presignedUrlKey,
    fileType,
    annotationUserName,
    initialDocUrl,
    licenseKey,
    canSave = true,
    onSave,
    showSaveButton = true,
    showHeader = true,
    headerActions,
    onReadyChange,
    onAppliedRedactionsChange,
    onScrollContainerReady,
    isActive = true,
    className,
  }: PdfEditorProps,
  ref: ForwardedRef<PdfEditorHandle>,
) {
  const viewerRef = useRef<HTMLDivElement>(null);
  const viewerInstanceRef = useRef<Awaited<
    ReturnType<typeof WebViewer>
  > | null>(null);
  const disposePromiseRef = useRef<Promise<void> | null>(null);
  const isInitializingRef = useRef(false);
  const settleTimeoutRef = useRef<number | null>(null);
  const canceledRef = useRef<boolean>(false);
  const activeContentEditorRef = useRef<{
    blur: () => void;
  } | null>(null);
  const isContentBoxEditingRef = useRef(false);
  const pendingFinalizeRef = useRef<Promise<void> | null>(null);
  const currentEditorModeRef = useRef<"content-edit" | "redact">(
    "content-edit",
  );
  const { theme } = useTheme();
  const [isReady, setIsReady] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isInitializingViewer, setIsInitializingViewer] = useState(false);
  const [documentError, setDocumentError] = useState<string | null>(null);
  const [hasAppliedRedactions, setHasAppliedRedactions] = useState(false);

  const {
    data: presignedUrl,
    isLoading: isLoadingFileUrl,
    isError: isErrorFileUrl,
  } = useQuery({
    queryKey: [queryKeys.matterFileImages, presignedUrlKey, "pdf-editor"],
    enabled: Boolean(presignedUrlKey) && !initialDocUrl,
    refetchOnMount: "always",
    staleTime: 0,
    gcTime: 0,
    queryFn: async () => {
      if (!presignedUrlKey) {
        return "";
      }
      const response = await generateS3ItemPreSignedUrlV1(fileType)({
        clientMethod: "get_object",
        key: presignedUrlKey,
      });
      return response.data.presignedUrl;
    },
  });

  const resolvedDocUrl = initialDocUrl ?? presignedUrl ?? "";
  const hasDocumentSource = Boolean(resolvedDocUrl);
  const resolvedFileName = useMemo(() => {
    if (!fileName) return "document.pdf";
    const { fileExtension } = getFileNameParts(fileName);
    if (!fileExtension) {
      return `${fileName}.pdf`;
    }
    return fileName;
  }, [fileName]);
  const resolvedTheme = isDarkTheme(theme) ? "dark" : "light";

  const applyViewerUISettings = useCallback(
    (instance: Awaited<ReturnType<typeof WebViewer>>) => {
      const customCss = `
        [data-element="documentControl"] { display: none !important; }
        [data-element="settingsThemeSection"] { display: none !important; }
        .theme-options { display: none !important; }
      `;

      instance.UI.disableElements([
        "documentControl",
        "settingsThemeSection",
        "themeButton",
        "toggleThemeButton",
        "saveAsButton",
        "filePickerButton",
        "toolbarGroup-FillAndSign",
      ]);

      if (!viewerRef.current) return;
      const existingStyle = viewerRef.current.querySelector(
        'style[data-pdf-ui="true"]',
      );
      if (existingStyle) {
        existingStyle.textContent = customCss;
        return;
      }
      const style = document.createElement("style");
      style.setAttribute("data-pdf-ui", "true");
      style.textContent = customCss;
      viewerRef.current.appendChild(style);
    },
    [],
  );

  const applyAnnotationUserSettings = useCallback(
    (instance: Awaited<ReturnType<typeof WebViewer>>) => {
      const trimmedUserName = annotationUserName?.trim();
      if (!trimmedUserName) return;

      instance.Core.annotationManager.setCurrentUser(trimmedUserName);
    },
    [annotationUserName],
  );

  const applyDefaultStickyNoteStyles = useCallback(
    (instance: Awaited<ReturnType<typeof WebViewer>>) => {
      const stickyNoteColor = new instance.Core.Annotations.Color(
        DEFAULT_STICKY_NOTE_COLOR,
      );
      const stickyToolNames = [
        instance.Core.Tools.ToolNames.STICKY,
        instance.Core.Tools.ToolNames.STICKY2,
        instance.Core.Tools.ToolNames.STICKY3,
        instance.Core.Tools.ToolNames.STICKY4,
      ];

      stickyToolNames.forEach((toolName) => {
        instance.Core.documentViewer.getTool(toolName).setStyles({
          StrokeColor: stickyNoteColor,
          Opacity: 1,
        });
      });
    },
    [],
  );

  const applyCustomRedactionSearchPatterns = useCallback(
    (instance: Awaited<ReturnType<typeof WebViewer>>) => {
      instance.UI.removeRedactionSearchPattern(
        instance.UI.RedactionSearchPatterns.CREDIT_CARDS,
      );
      instance.UI.removeRedactionSearchPattern(
        instance.UI.RedactionSearchPatterns.EMAILS,
      );
      instance.UI.removeRedactionSearchPattern(
        instance.UI.RedactionSearchPatterns.PHONE_NUMBERS,
      );

      [...REDACTION_SEARCH_PATTERNS]
        .sort((left, right) => left.label.localeCompare(right.label))
        .forEach((pattern) => {
          instance.UI.addRedactionSearchPattern({
            label: pattern.label,
            type: pattern.type,
            regex: pattern.regex,
          });
        });
    },
    [],
  );

  const disposeViewer = useCallback(async () => {
    if (disposePromiseRef.current) {
      await disposePromiseRef.current;
    }
    const instance = viewerInstanceRef.current;
    if (!instance) return;
    const disposePromise = instance.UI.dispose();
    disposePromiseRef.current = disposePromise;
    viewerInstanceRef.current = null;
    try {
      await disposePromise;
    } finally {
      if (disposePromiseRef.current === disposePromise) {
        disposePromiseRef.current = null;
      }
    }
  }, []);

  const resizeViewer = useCallback(() => {
    const instance = viewerInstanceRef.current;
    if (!instance) return;
    const ui = instance.UI as unknown as { resize?: () => void };
    ui.resize?.();
  }, []);

  const applyDefaultZoom = useCallback(
    (instance: Awaited<ReturnType<typeof WebViewer>>) => {
      instance.UI.setZoomLevel(DEFAULT_PDF_EDITOR_ZOOM);
    },
    [],
  );

  const disableTextToolHotkeys = useCallback(
    (instance: Awaited<ReturnType<typeof WebViewer>>) => {
      instance.UI.hotkeys.off(instance.UI.hotkeys.Keys.N);
      instance.UI.hotkeys.off(instance.UI.hotkeys.Keys.T);
    },
    [],
  );

  const activateContentEditTool = useCallback(
    (instance: Awaited<ReturnType<typeof WebViewer>>) => {
      const contentEditToolName = String(
        instance.Core.Tools.ToolNames.CONTENT_EDIT,
      );
      instance.UI.setToolbarGroup(instance.UI.ToolbarGroup.EDIT, false);
      const activeTool = instance.Core.documentViewer.getToolMode();
      if (activeTool.name !== contentEditToolName) {
        instance.UI.setToolMode(contentEditToolName);
      }
    },
    [],
  );

  const activateIdleContentTool = useCallback(
    (instance: Awaited<ReturnType<typeof WebViewer>>) => {
      const preferredToolName = String(instance.Core.Tools.ToolNames.PAN);
      const activeTool = instance.Core.documentViewer.getToolMode();

      instance.UI.setToolbarGroup(instance.UI.ToolbarGroup.EDIT, false);

      if (activeTool.name !== preferredToolName) {
        instance.UI.setToolMode(preferredToolName);
      }
    },
    [],
  );

  const waitForViewerUpdate = useCallback(async (delayMs = 200) => {
    await new Promise((resolve) => {
      window.setTimeout(() => {
        resolve(null);
      }, delayMs);
    });
  }, []);

  useEffect(() => {
    const viewerElement = viewerRef.current;
    if (!viewerElement) return;

    canceledRef.current = false;
    const initializeViewer = async () => {
      setIsReady(false);
      setIsInitializingViewer(true);
      setDocumentError(null);
      if (isInitializingRef.current) {
        setIsInitializingViewer(false);
        return;
      }
      isInitializingRef.current = true;
      try {
        if (!isActive) {
          setIsInitializingViewer(false);
          setDocumentError(null);
          setIsReady(false);
          setHasAppliedRedactions(false);
          await disposeViewer();
          viewerElement.innerHTML = "";
          return;
        }

        if (!resolvedDocUrl) {
          setIsInitializingViewer(false);
          setIsReady(false);
          setHasAppliedRedactions(false);
          return;
        }

        await new Promise((resolve) =>
          requestAnimationFrame(() => {
            resolve(null);
          }),
        );

        if (canceledRef.current) {
          setIsInitializingViewer(false);
          return;
        }

        await disposeViewer();

        viewerElement.innerHTML = "";

        const instance = await WebViewer(
          {
            path: "/lib/webviewer",
            licenseKey,
            fullAPI: true,
            enableRedaction: true,
            extension: "pdf",
            filename: resolvedFileName,
            initialDoc: resolvedDocUrl,
          },
          viewerElement,
        );

        viewerInstanceRef.current = instance;
        applyAnnotationUserSettings(instance);
        applyDefaultStickyNoteStyles(instance);
        instance.UI.enableFeatures([
          instance.UI.Feature.ContentEdit,
          instance.UI.Feature.Redaction,
        ]);
        disableTextToolHotkeys(instance);
        applyCustomRedactionSearchPatterns(instance);
        applyViewerUISettings(instance);

        const documentViewer = instance.Core.documentViewer;
        const contentEditManager = documentViewer.getContentEditManager();
        const annotationManager = instance.Core.annotationManager;
        const handleContentBoxEditStarted = (event?: {
          editor?: { blur: () => void };
        }) => {
          isContentBoxEditingRef.current = true;
          activeContentEditorRef.current = event?.editor ?? null;
          logPdfEditorDebug("contentBoxEditStarted", {
            fileName: resolvedFileName,
            hasEditor: Boolean(event?.editor),
          });
        };
        const handleContentBoxEditEnded = () => {
          isContentBoxEditingRef.current = false;
          activeContentEditorRef.current = null;
          if (currentEditorModeRef.current === "content-edit") {
            activateContentEditTool(instance);
          }
          // Apryse restores its default shortcuts after each content box edit.
          disableTextToolHotkeys(instance);
          logPdfEditorDebug("contentBoxEditEnded", {
            fileName: resolvedFileName,
          });
        };
        const handleAnnotationChanged = (
          _annotations: unknown,
          _action: string,
          info?: { source?: string },
        ) => {
          if (info?.source === "redactionApplied") {
            setHasAppliedRedactions(true);
          }
        };
        const handleDocumentLoaded = async () => {
          try {
            currentEditorModeRef.current = "content-edit";
            setHasAppliedRedactions(false);
            annotationManager.enableRedaction();
            await instance.Core.ContentEdit.preloadWorker(contentEditManager);
            if (!contentEditManager.isInContentEditMode()) {
              await contentEditManager.startContentEditMode();
            }

            if (settleTimeoutRef.current) {
              window.clearTimeout(settleTimeoutRef.current);
            }
            await new Promise((resolve) => {
              settleTimeoutRef.current = window.setTimeout(() => {
                resolve(null);
              }, 220);
            });
            if (canceledRef.current) return;

            resizeViewer();
            activateContentEditTool(instance);
            disableTextToolHotkeys(instance);
            applyDefaultZoom(instance);
            logPdfEditorDebug("documentLoaded", {
              fileName: resolvedFileName,
              zoom: DEFAULT_PDF_EDITOR_ZOOM,
            });
            setIsReady(true);
            setIsInitializingViewer(false);
          } catch (error) {
            console.error("Failed to start PDF content editing", error);
            if (!canceledRef.current) {
              setDocumentError("Failed to start PDF editing.");
              setIsInitializingViewer(false);
            }
          } finally {
            documentViewer.removeEventListener(
              "documentLoaded",
              handleDocumentLoaded,
            );
          }
        };
        const handleDocumentLoadFailed = () => {
          if (canceledRef.current) return;
          setDocumentError("Failed to load document.");
          setIsInitializingViewer(false);
          documentViewer.removeEventListener(
            "documentLoadFailed",
            handleDocumentLoadFailed,
          );
        };

        contentEditManager.addEventListener(
          "contentBoxEditStarted",
          handleContentBoxEditStarted as () => void,
        );
        contentEditManager.addEventListener(
          "contentBoxEditEnded",
          handleContentBoxEditEnded,
        );
        annotationManager.addEventListener(
          "annotationChanged",
          handleAnnotationChanged,
        );
        documentViewer.addEventListener("documentLoaded", handleDocumentLoaded);
        documentViewer.addEventListener(
          "documentLoadFailed",
          handleDocumentLoadFailed,
        );
      } catch (error) {
        console.error("Failed to initialize PDF editor", error);
        if (!canceledRef.current) {
          setDocumentError("Failed to load document.");
          setIsInitializingViewer(false);
        }
      } finally {
        isInitializingRef.current = false;
      }
    };

    void initializeViewer();

    return () => {
      canceledRef.current = true;
    };
  }, [
    activateIdleContentTool,
    activateContentEditTool,
    applyDefaultZoom,
    disableTextToolHotkeys,
    applyViewerUISettings,
    applyAnnotationUserSettings,
    applyCustomRedactionSearchPatterns,
    applyDefaultStickyNoteStyles,
    disposeViewer,
    isActive,
    licenseKey,
    resolvedDocUrl,
    resolvedFileName,
    resizeViewer,
  ]);

  useEffect(() => {
    return () => {
      void disposeViewer();
      if (settleTimeoutRef.current) {
        window.clearTimeout(settleTimeoutRef.current);
        settleTimeoutRef.current = null;
      }
      isInitializingRef.current = false;
    };
  }, [disposeViewer]);

  useEffect(() => {
    if (!viewerInstanceRef.current) return;
    viewerInstanceRef.current.UI.setTheme(resolvedTheme);
  }, [isReady, resolvedTheme]);

  useEffect(() => {
    if (!onScrollContainerReady) return;
    const viewerElement = viewerRef.current;
    if (!viewerElement || !isReady) {
      onScrollContainerReady(null);
      return;
    }

    const getScrollContainer = () =>
      findElementInOpenShadowRoots(
        viewerElement,
        '[data-element="documentContainer"], .DocumentContainer',
      ) as HTMLDivElement | null;

    const emitScrollContainer = () => {
      onScrollContainerReady(getScrollContainer());
    };

    emitScrollContainer();

    const observer = new MutationObserver(() => {
      emitScrollContainer();
    });

    observer.observe(viewerElement, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      onScrollContainerReady(null);
    };
  }, [isReady, onScrollContainerReady]);

  useEffect(() => {
    const instance = viewerInstanceRef.current;
    if (!instance) return;

    applyAnnotationUserSettings(instance);
  }, [applyAnnotationUserSettings]);

  const startContentEditing = useCallback(async () => {
    const instance = viewerInstanceRef.current;
    if (!instance) return;
    const contentEditManager =
      instance.Core.documentViewer.getContentEditManager();
    instance.Core.annotationManager.enableRedaction();
    if (!contentEditManager.isInContentEditMode()) {
      await instance.Core.ContentEdit.preloadWorker(contentEditManager);
      await contentEditManager.startContentEditMode();
      logPdfEditorDebug("startContentEditMode", {
        fileName: resolvedFileName,
      });
    }
    currentEditorModeRef.current = "content-edit";
    activateContentEditTool(instance);
    resizeViewer();
    applyDefaultZoom(instance);
  }, [
    activateContentEditTool,
    applyDefaultZoom,
    resizeViewer,
    resolvedFileName,
  ]);

  const finalizeActiveTextEdit = useCallback(async () => {
    if (pendingFinalizeRef.current) {
      await pendingFinalizeRef.current;
      return;
    }

    const instance = viewerInstanceRef.current;
    if (!instance) return;
    const contentEditManager =
      instance.Core.documentViewer.getContentEditManager();

    if (!isContentBoxEditingRef.current || !activeContentEditorRef.current) {
      activateIdleContentTool(instance);
      return;
    }

    const finalizePromise = new Promise<void>((resolve) => {
      let settled = false;
      const cleanup = () => {
        if (settled) return;
        settled = true;
        contentEditManager.removeEventListener(
          "contentBoxEditEnded",
          handleContentBoxEditEnded,
        );
        window.clearTimeout(timeoutId);
        pendingFinalizeRef.current = null;
        resolve();
      };
      const handleContentBoxEditEnded = () => {
        isContentBoxEditingRef.current = false;
        activeContentEditorRef.current = null;
        activateIdleContentTool(instance);
        cleanup();
      };
      const timeoutId = window.setTimeout(() => {
        cleanup();
      }, 500);

      contentEditManager.addEventListener(
        "contentBoxEditEnded",
        handleContentBoxEditEnded,
      );

      logPdfEditorDebug("finalizeActiveTextEdit:blurActiveEditor", {
        fileName: resolvedFileName,
      });
      activeContentEditorRef.current?.blur();
    });

    pendingFinalizeRef.current = finalizePromise;
    await finalizePromise;
  }, [activateIdleContentTool, resolvedFileName]);

  const startRedactionMode = useCallback(async () => {
    const instance = viewerInstanceRef.current;
    if (!instance) return;

    const documentViewer = instance.Core.documentViewer;
    const contentEditManager = documentViewer.getContentEditManager();
    const annotationManager = instance.Core.annotationManager;

    await finalizeActiveTextEdit();

    if (contentEditManager.isInContentEditMode()) {
      contentEditManager.endContentEditMode();
      await waitForViewerUpdate();
    }

    annotationManager.enableRedaction();
    currentEditorModeRef.current = "redact";
    resizeViewer();
    applyDefaultZoom(instance);
  }, [
    applyDefaultZoom,
    finalizeActiveTextEdit,
    resizeViewer,
    waitForViewerUpdate,
  ]);

  useEffect(() => {
    const instance = viewerInstanceRef.current;
    if (!instance || !isReady) return;

    const { documentViewer, Tools } = instance.Core;
    const contentEditToolName = String(Tools.ToolNames.CONTENT_EDIT);
    const redactionToolNames = new Set([
      String(Tools.ToolNames.REDACTION),
      String(Tools.ToolNames.REDACTION2),
      String(Tools.ToolNames.REDACTION3),
      String(Tools.ToolNames.REDACTION4),
    ]);

    const handleToolModeUpdated = (newToolObject?: { name?: string }) => {
      const toolName = newToolObject?.name;
      if (!toolName) return;

      if (redactionToolNames.has(toolName)) {
        void startRedactionMode();
        return;
      }

      if (toolName === contentEditToolName) {
        void startContentEditing();
      }
    };

    documentViewer.addEventListener("toolModeUpdated", handleToolModeUpdated);

    return () => {
      documentViewer.removeEventListener(
        "toolModeUpdated",
        handleToolModeUpdated,
      );
    };
  }, [isReady, startContentEditing, startRedactionMode]);

  const hasPendingRedactions = useCallback(() => {
    const instance = viewerInstanceRef.current;
    if (!instance) return false;

    return instance.Core.annotationManager
      .getAnnotationsList()
      .some(
        (annotation) =>
          annotation instanceof instance.Core.Annotations.RedactionAnnotation,
      );
  }, []);

  const exportDocument = useCallback(
    async (options: PdfEditorExportOptions = {}) => {
      const { applyRedactions = false } = options;
      const instance = viewerInstanceRef.current;
      if (!instance) return null;
      const documentViewer = instance.Core.documentViewer;
      const contentEditManager = documentViewer.getContentEditManager();
      const annotationManager = instance.Core.annotationManager;
      const wasInContentEditMode = contentEditManager.isInContentEditMode();

      try {
        logPdfEditorDebug("exportDocument:start", {
          fileName: resolvedFileName,
          applyRedactions,
          wasInContentEditMode,
          isContentBoxEditing: isContentBoxEditingRef.current,
        });

        await finalizeActiveTextEdit();

        if (wasInContentEditMode) {
          logPdfEditorDebug("exportDocument:endContentEditMode", {
            fileName: resolvedFileName,
          });
          contentEditManager.endContentEditMode();
          await waitForViewerUpdate();

          if (typeof instance.Core.ContentEdit.regeneratePage === "function") {
            logPdfEditorDebug("exportDocument:regeneratePage", {
              fileName: resolvedFileName,
            });
            await instance.Core.ContentEdit.regeneratePage();
            await waitForViewerUpdate(150);
          } else {
            logPdfEditorDebug("exportDocument:regeneratePageUnavailable", {
              fileName: resolvedFileName,
            });
          }
        }

        const redactionAnnotations = annotationManager
          .getAnnotationsList()
          .filter(
            (annotation) =>
              annotation instanceof
              instance.Core.Annotations.RedactionAnnotation,
          );

        if (applyRedactions && redactionAnnotations.length > 0) {
          logPdfEditorDebug("exportDocument:applyRedactions", {
            fileName: resolvedFileName,
            count: redactionAnnotations.length,
          });
          await annotationManager.applyRedactions(redactionAnnotations);
          await waitForViewerUpdate(150);
        }

        const xfdfString = await annotationManager.exportAnnotations();
        logPdfEditorDebug("exportDocument:annotationsExported", {
          fileName: resolvedFileName,
          xfdfLength: xfdfString.length,
        });

        const fileData = await documentViewer.getDocument().getFileData({
          xfdfString,
        });
        const blob = new Blob([fileData], { type: PDF_MIME_TYPE });
        logPdfEditorDebug(
          "exportDocument:blobReady",
          await getBlobDebugSummary(blob),
        );
        return blob;
      } finally {
        if (
          currentEditorModeRef.current === "content-edit" &&
          viewerInstanceRef.current === instance &&
          !canceledRef.current
        ) {
          try {
            await startContentEditing();
          } catch (error) {
            console.error("Failed to restore PDF editing after export", error);
          }
        }
      }
    },
    [
      finalizeActiveTextEdit,
      resolvedFileName,
      startContentEditing,
      waitForViewerUpdate,
    ],
  );

  const handleSave = useCallback(async () => {
    if (!onSave) return;
    setIsSaving(true);
    try {
      await finalizeActiveTextEdit();
      const blob = await exportDocument();
      if (!blob) return;
      await onSave({ blob, fileName: resolvedFileName });
      await disposeViewer();
      setIsReady(false);
    } catch (error) {
      console.error("Failed to save PDF document", error);
      toast.error("Failed to save document");
    } finally {
      setIsSaving(false);
    }
  }, [
    disposeViewer,
    exportDocument,
    finalizeActiveTextEdit,
    onSave,
    resolvedFileName,
  ]);

  useImperativeHandle(
    ref,
    () => ({
      exportDocument,
      hasAppliedRedactions: () => hasAppliedRedactions,
      hasPendingRedactions,
      isReady,
      isLoading: isLoadingFileUrl || isInitializingViewer,
    }),
    [
      exportDocument,
      hasAppliedRedactions,
      hasPendingRedactions,
      isLoadingFileUrl,
      isInitializingViewer,
      isReady,
    ],
  );

  useEffect(() => {
    if (!isActive) return;
    onReadyChange?.(isReady);
  }, [isActive, isReady, onReadyChange]);

  useEffect(() => {
    if (!isActive) return;
    onAppliedRedactionsChange?.(hasAppliedRedactions);
  }, [hasAppliedRedactions, isActive, onAppliedRedactionsChange]);

  const isLoadingDocument = isLoadingFileUrl || isInitializingViewer;
  const hasDocumentError = Boolean(
    documentError || isErrorFileUrl || !hasDocumentSource,
  );

  return (
    <div className={cn("flex min-h-0 w-full flex-1 flex-col gap-3", className)}>
      {showHeader && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="text-primary h-5 w-5" />
            <div className="flex flex-col">
              <Typography variant="subHeading">PDF Editor</Typography>
              <Typography className="text-muted-foreground text-sm">
                Edit content or mark redactions before saving.
              </Typography>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {headerActions}
            {onSave && showSaveButton && (
              <Button
                type="button"
                size="sm"
                variant="light-primary"
                onMouseDown={() => {
                  void finalizeActiveTextEdit();
                }}
                onClick={() => {
                  void handleSave();
                }}
                disabled={!canSave || isSaving || !isReady || isLoadingDocument}
              >
                {isSaving ? <LoadingSpinner /> : "Save"}
              </Button>
            )}
          </div>
        </div>
      )}
      <div className="bg-card relative min-h-0 w-full flex-1 overflow-hidden rounded-lg border">
        {isLoadingDocument && (
          <div className="text-muted-foreground absolute inset-0 grid place-items-center text-sm">
            Loading document...
          </div>
        )}
        {hasDocumentError && !isLoadingDocument && (
          <div className="text-destructive absolute inset-0 grid place-items-center text-sm">
            {documentError ??
              (hasDocumentSource
                ? "Failed to load document."
                : "No document available.")}
          </div>
        )}
        <div className="h-full min-h-0 w-full p-3 pr-6">
          <div
            ref={viewerRef}
            className={cn(
              "h-full w-full min-w-0 overflow-visible rounded-md",
              isLoadingDocument && "opacity-50",
            )}
          />
        </div>
      </div>
    </div>
  );
});
